import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Download, ChevronLeft, ChevronRight } from "lucide-react";

const STATUSES = ["awaiting_payment", "payment_pending", "paid", "queued", "calculating",
  "generating_report", "validating_pdf", "scheduled_for_delivery", "delivered",
  "generation_failed", "delivery_failed", "cancelled", "refunded"];

const BADGE = {
  delivered: "bg-emerald-100 text-emerald-800", scheduled_for_delivery: "bg-indigo-100 text-indigo-800",
  paid: "bg-sky-100 text-sky-800", queued: "bg-sky-100 text-sky-800",
  generating_report: "bg-violet-100 text-violet-800", calculating: "bg-violet-100 text-violet-800",
  validating_pdf: "bg-violet-100 text-violet-800",
  awaiting_payment: "bg-amber-100 text-amber-800", payment_pending: "bg-amber-100 text-amber-800",
  generation_failed: "bg-rose-100 text-rose-800", delivery_failed: "bg-rose-100 text-rose-800",
  cancelled: "bg-slate-200 text-slate-700", refunded: "bg-slate-200 text-slate-700",
};

export default function Orders() {
  const [data, setData] = useState({ orders: [], total: 0 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const limit = 20;

  const load = useCallback(() => {
    const params = { page, limit };
    if (q) params.q = q;
    if (status) params.status = status;
    api.get("/admin/orders", { params }).then((r) => setData(r.data)).catch(() => {});
  }, [q, status, page]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    const r = await api.get("/admin/export", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = "orders.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const pages = Math.ceil(data.total / limit) || 1;

  return (
    <div data-testid="admin-orders">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="font-serif text-3xl text-navy">Orders</h1>
        <button onClick={exportCsv} data-testid="export-orders" className="btn-ghost text-sm self-start"><Download className="w-4 h-4" /> Export CSV</button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="orders-search" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }}
            placeholder="Search order ID, name, or email" className="pl-9" aria-label="Search orders" />
        </div>
        <Select value={status || "all"} onValueChange={(v) => { setPage(1); setStatus(v === "all" ? "" : v); }}>
          <SelectTrigger data-testid="orders-status-filter" className="sm:w-56" aria-label="Filter status"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm" data-testid="orders-table">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="py-3 px-4 font-medium">Order ID</th>
                <th className="py-3 px-4 font-medium">Customer</th>
                <th className="py-3 px-4 font-medium">Package</th>
                <th className="py-3 px-4 font-medium">Amount</th>
                <th className="py-3 px-4 font-medium">Status</th>
                <th className="py-3 px-4 font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {data.orders.map((o) => (
                <tr key={o.order_id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4">
                    <Link to={`/admin/orders/${o.order_id}`} data-testid={`order-row-${o.order_id}`} className="font-mono text-xs text-gold-hover hover:underline">{o.order_id}</Link>
                  </td>
                  <td className="py-3 px-4">
                    <p className="text-slate-900">{o.customer.full_name}</p>
                    <p className="text-xs text-slate-400">{o.customer.email}</p>
                  </td>
                  <td className="py-3 px-4 text-slate-600">{o.package_name}</td>
                  <td className="py-3 px-4 text-slate-900">₹{o.amount}</td>
                  <td className="py-3 px-4"><span className={`text-xs px-2.5 py-1 rounded-full ${BADGE[o.status] || "bg-slate-100 text-slate-600"}`}>{o.status.replace(/_/g, " ")}</span></td>
                  <td className="py-3 px-4 text-xs text-slate-400">{o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
              {data.orders.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400">No orders found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between mt-5 text-sm text-slate-500">
        <span>{data.total} orders</span>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="p-2 rounded-lg border border-slate-200 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
          <span>Page {page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="p-2 rounded-lg border border-slate-200 disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}
